import {useEffect, useState} from "react";
import axios from "axios";
import {Form, useLoaderData, useRouteLoaderData, NavLink, useNavigate} from "react-router-dom";
import {
    Button,
    Grid,
    Paper,
    Typography,
    Box,
    Divider,
    Tooltip,
    Checkbox,
    Stack,
    Slider,
    Chip,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    ListItemText, FormControlLabel, TextField, IconButton
} from "@mui/material";
import {styled} from '@mui/material/styles';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import {sanitizeAndCrop} from "../../hooks/reformatPermissions";
import PlaceholderedImage from "../../components/placeholderImage";
import {isBefore, parseISO, isEqual} from "date-fns";
import {showSnackbar} from "../../components/snackbarUtils";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import {Dialog, Container} from '@mui/material';
import {useSearchParams} from 'react-router-dom';
import PageTitle from "../../components/PageTitle";


const CustomSlider = styled(Slider)({
    '& .MuiSlider-markLabel': {
        display: 'none', // Hide all labels initially
    },
})

export async function loader({request, params}) {
    const searchParams = (new URL(request.url)).searchParams;
    const init_url = '/api/booking_modules/use/?'
    const search = searchParams.toString() || '';

    if (search === '') {
        return {error: true, message: 'Вкажіть параметри пошуку, щоб почати'}
    }

    const city = searchParams.get('city');
    const booking_from = searchParams.get('booking_from');
    const booking_to = searchParams.get('booking_to');

    const cacheKey = `booking_search_${city}_${booking_from}_${booking_to}`;
    const cachedData = localStorage.getItem(cacheKey);

    const CACHE_DURATION_MS = 10 * 60 * 1000; // 10 минут

    if (cachedData) {
        try {
            const parsed = JSON.parse(cachedData);
            const now = Date.now();

            if (parsed.timestamp && now - parsed.timestamp < CACHE_DURATION_MS) {
                //console.log('Loaded search data from cache ✅');
                return {data: parsed.data};
            } else {
                // Устаревший кеш — удалить
                localStorage.removeItem(cacheKey);
            }
        } catch (e) {
            //console.error('Ошибка чтения кеша поиска:', e);
            localStorage.removeItem(cacheKey);
        }
    }

    // Если кеша нет или он протух — делаем запрос
    const response = await axios.get(init_url + search);
    const freshData = response.data;

    // Сохраняем в кеш
    localStorage.setItem(cacheKey, JSON.stringify({
        data: freshData,
        timestamp: Date.now()
    }));

    //const data = (await axios.get(init_url + search)).data;
    return {"data": freshData};
}

export default function SearchIndexPage() {

    const navigate = useNavigate();

const hotelDetailsPath = (hotel, stringifiedSearch) => {
    const identifier = hotel?.slug || hotel?.id;
    return `/hotels/${identifier}?${stringifiedSearch}`;
};
    const {data} = useLoaderData();
    const [originalData, setOriginalData] = useState(data)
    const [filteredData, setFilteredData] = useState(data);
    // const {filter, searchParams: searchParams, stringifiedSearch} = useRouteLoaderData('book_layout');

    const {filter, stringifiedSearch} = useRouteLoaderData('book_layout');
    const [urlSearchParams, setUrlSearchParams] = useSearchParams();
    const searchParams = urlSearchParams;

    const bookingFrom = searchParams.get('booking_from');
    const bookingTo = searchParams.get('booking_to');

    const numNights = bookingFrom && bookingTo
        ? Math.max(
            1,
            (parseISO(bookingTo) - parseISO(bookingFrom)) / (1000 * 60 * 60 * 24)
        )
        : null;

    useEffect(() => {
        // Новая логика фильтрации
        const filtered = originalData.filter((item) => {
            const hotel = item.hotel;

            // Исключить отель, если у него есть single_hotel
            if (hotel.single_hotel && Array.isArray(hotel.single_hotel) && hotel.single_hotel.length > 0) {
                return false;
            }

            const firstChild = hotel.matching_children?.[0] || hotel.remaining_children?.[0];
            if (!firstChild) return false;

            const withAnimals = searchParams.get("with_animals");
            if (withAnimals && firstChild.with_animals_choices !== withAnimals) return false;

            const selectedMeals = new Set(
                searchParams
                    .getAll("meals")
                    .map(m => String(m).trim().toLowerCase())
                    .filter(Boolean)
            );

            if (selectedMeals.size > 0) {
                // предполагаем, что firstChild.meal может быть строкой типа "breakfast,lunch,dinner"
                const childMeals = new Set(
                    String(firstChild?.meal || "")
                        .split(",")
                        .map(m => m.trim().toLowerCase())
                        .filter(Boolean)
                );

                // показываем, если есть ХОТЯ БЫ ОДНО совпадение
                const ok = [...selectedMeals].some(m => childMeals.has(m));
                if (!ok) return false;
            }

            const priceFrom = parseInt(searchParams.get("price_from"));
            const priceTo = parseInt(searchParams.get("price_to"));
            const totalPrice = firstChild.total_price;
            if ((priceFrom && totalPrice < priceFrom) || (priceTo && totalPrice > priceTo)) return false;

            return true;
        });

        // --- ДОБАВИТЬ single_hotel если он есть ---
        const singleHotelItems = [];
        originalData.forEach(item => {
            const hotel = item.hotel;
            if (hotel.single_hotel && Array.isArray(hotel.single_hotel) && hotel.single_hotel.length > 0) {
                singleHotelItems.push({
                    hotel: {
                        ...hotel,
                        highlight_only: true
                    }
                });
            }
        });

        setFilteredData([...filtered, ...singleHotelItems]);
    }, [searchParams, originalData]);


    useEffect(() => {
        setOriginalData(data);
        setFilteredData(data);
    }, [data]);

    const updateFilter = (key, value) => {
        const newParams = new URLSearchParams(urlSearchParams);
        if (Array.isArray(value)) {
            newParams.delete(key);
            value.forEach(v => newParams.append(key, v));
        } else {
            if (value === '' || value === null || value === undefined) {
                newParams.delete(key);
            } else {
                newParams.set(key, value);
            }
        }

        setUrlSearchParams(newParams);
    };

    const pluralNightsUa = (n) => {
        const i = Math.abs(Number(n)) % 100;
        const j = i % 10;
        if (i > 10 && i < 20) return 'ночей';
        if (j === 1) return 'ночі';
        if (j >= 2 && j <= 4) return 'ночей';
        return 'ночей';
    };


    // Состояние для выбранных классификаций
    const [selectedClassifications, setSelectedClassifications] = useState(searchParams?.classification || []);

    const [meals, setMeals] = useState(searchParams?.meals || []);
    const [petsAllowed, setPetsAllowed] = useState(searchParams?.pets_allowed?.[0] === 'true');

    const handleClassificationChange = (event) => {
        const {
            target: {value},
        } = event;
        const updatedClassifications = typeof value === 'string' ? value.split(',') : value;

        setSelectedClassifications(updatedClassifications); // Обновляем локально
        updateFilter('classification', updatedClassifications); // Обновляем в URL
    };

    const [sliderValue, setSliderValue] = useState(() => {
        const minPrice = filter.price.length ? filter.price[0] : 0;
        const maxPrice = filter.price.length ? filter.price[filter.price.length - 1] : 1000000;

        return [
            Number(searchParams?.price_from ?? minPrice),
            Number(searchParams?.price_to ?? maxPrice)
        ];
    });

    const mealLabels = {
        breakfast: "Сніданок",
        lunch: "Обід",
        dinner: "Вечеря"
    };

    const generateSliderMarks = (prices) => {
        if (!prices.length) return [];

        const uniquePrices = Array.from(new Set(prices)); // убрать дубли
        const step = Math.ceil(uniquePrices.length / 5); // каждые 5 меток максимум

        const marks = uniquePrices
            .filter((_, index) => index % step === 0) // оставить метку через step
            .map(value => ({
                value: value,
                label: `${value} UAH`
            }));

        // обязательно добавить первую и последнюю цену
        if (marks[0]?.value !== uniquePrices[0]) {
            marks.unshift({
                value: uniquePrices[0],
                label: `${uniquePrices[0]} UAH`
            });
        }
        if (marks[marks.length - 1]?.value !== uniquePrices[uniquePrices.length - 1]) {
            marks.push({
                value: uniquePrices[uniquePrices.length - 1],
                label: `${uniquePrices[uniquePrices.length - 1]} UAH`
            });
        }

        return marks;
    };

    const sliderMarks = generateSliderMarks(filter.price);

    const filteredKeys = ['classification', 'price_from', 'price_to', 'meals', 'pets_allowed'];

    const filteredObject = Object.fromEntries(
        Object.entries(searchParams)
            .filter(([key]) => !filteredKeys.includes(key))
    );

    const handleMealsChange = (event) => {
        const {
            target: {value},
        } = event;
        const updatedMeals = typeof value === 'string' ? value.split(',') : value;

        setMeals(updatedMeals); // Локально обновляем
        updateFilter('meals', updatedMeals); // Моментально меняем URL-параметры
    };


    const [numAdults, setNumAdults] = useState(parseInt(searchParams?.adults?.[0] ?? 1, 10));
    const [numChildren, setNumChildren] = useState(parseInt(searchParams?.children?.[0] ?? 0, 10));
    const [numRooms, setNumRooms] = useState(parseInt(searchParams?.rooms?.[0] ?? 1, 10));
    const [childrenAges, setChildrenAges] = useState(searchParams?.children_ages?.map(Number) ?? []);
    const [openDialogPeople, setOpenDialogPeople] = useState(false);

    const isMobile = window.innerWidth <= 600;

    return (
        <>
            <PageTitle title="Пошук об'єктів"/>
            <Box>
                <Box sx={{width: '100%', my: 1}}>
                    <Divider orientation='horizontal'/>
                </Box>
                <Grid container spacing={1}>
                    {/*<Grid item sm={4} xs={12}>*/}
                    {/*    /!*<Form method="get">*!/*/}
                    {/*    <Grid item component={Paper} container xs={12} sx={{my: 1, p: 2, height: 'fit-content'}}>*/}
                    {/*        /!*<Grid item sx={{my: 1}} xs={12}>*!/*/}
                    {/*        /!*    <FormControl fullWidth>*!/*/}
                    {/*        /!*        <TextField*!/*/}
                    {/*        /!*            value={`${numAdults} дорослих, ${numChildren} дітей, ${numRooms} номерів`}*!/*/}
                    {/*        /!*            InputProps={{readOnly: true}}*!/*/}
                    {/*        /!*            onClick={() => setOpenDialogPeople(true)}*!/*/}
                    {/*        /!*            label="Гості"*!/*/}
                    {/*        /!*        />*!/*/}
                    {/*        /!*    </FormControl>*!/*/}

                    {/*        /!*    <input type='hidden' name='adults' value={numAdults}/>*!/*/}
                    {/*        /!*    <input type='hidden' name='children' value={numChildren}/>*!/*/}
                    {/*        /!*    <input type='hidden' name='rooms' value={numRooms}/>*!/*/}
                    {/*        /!*    {childrenAges.map((age, index) => (*!/*/}
                    {/*        /!*        <input key={index} type="hidden" name="children_ages" value={age}/>*!/*/}
                    {/*        /!*    ))}*!/*/}

                    {/*        /!*    <Dialog open={openDialogPeople} onClose={() => setOpenDialogPeople(false)}*!/*/}
                    {/*        /!*            fullScreen={isMobile}>*!/*/}
                    {/*        /!*        <Container maxWidth="xs"*!/*/}
                    {/*        /!*                   sx={{py: 2, width: '100%', minHeight: isMobile ? '100vh' : 'auto'}}>*!/*/}
                    {/*        /!*            <Stack spacing={2} sx={{width: '100%'}} direction="column">*!/*/}
                    {/*        /!*                <Grid container spacing={1}>*!/*/}
                    {/*        /!*                    <ChangeNumberComponent*!/*/}
                    {/*        /!*                        handleChangeValue={(delta) => setNumAdults(prev => Math.max(prev + delta, 1))}*!/*/}
                    {/*        /!*                        disableConditionMinus={numAdults <= 1}*!/*/}
                    {/*        /!*                        value={numAdults}*!/*/}
                    {/*        /!*                        label="Дорослі"*!/*/}
                    {/*        /!*                    />*!/*/}
                    {/*        /!*                    <ChangeNumberComponent*!/*/}
                    {/*        /!*                        handleChangeValue={(delta) => setNumChildren(prev => Math.max(prev + delta, 0))}*!/*/}
                    {/*        /!*                        disableConditionMinus={numChildren <= 0}*!/*/}
                    {/*        /!*                        value={numChildren}*!/*/}
                    {/*        /!*                        label="Діти"*!/*/}
                    {/*        /!*                    />*!/*/}
                    {/*        /!*                    <ChangeNumberComponent*!/*/}
                    {/*        /!*                        handleChangeValue={(delta) => setNumRooms(prev => Math.max(prev + delta, 1))}*!/*/}
                    {/*        /!*                        disableConditionMinus={numRooms <= 1}*!/*/}
                    {/*        /!*                        value={numRooms}*!/*/}
                    {/*        /!*                        label="Номери"*!/*/}
                    {/*        /!*                    />*!/*/}
                    {/*        /!*                </Grid>*!/*/}

                    {/*        /!*                {numChildren > 0 && (*!/*/}
                    {/*        /!*                    <>*!/*/}
                    {/*        /!*                        <Grid container>*!/*/}
                    {/*        /!*                            <Typography>Вкажіть вік дітей</Typography>*!/*/}
                    {/*        /!*                        </Grid>*!/*/}
                    {/*        /!*                        <Grid container>*!/*/}
                    {/*        /!*                            {Array.from({length: numChildren}, (_, index) => (*!/*/}
                    {/*        /!*                                <Grid item xs={6} sx={{p: 0.5}} key={index}>*!/*/}
                    {/*        /!*                                    <FormControl fullWidth>*!/*/}
                    {/*        /!*                                        <InputLabel*!/*/}
                    {/*        /!*                                            id={`child_age_${index}`}>Вік</InputLabel>*!/*/}
                    {/*        /!*                                        <Select*!/*/}
                    {/*        /!*                                            labelId={`child_age_${index}`}*!/*/}
                    {/*        /!*                                            value={childrenAges[index] ?? ""}*!/*/}
                    {/*        /!*                                            onChange={(e) => {*!/*/}
                    {/*        /!*                                                const updated = [...childrenAges];*!/*/}
                    {/*        /!*                                                updated[index] = e.target.value;*!/*/}
                    {/*        /!*                                                setChildrenAges(updated);*!/*/}
                    {/*        /!*                                            }}*!/*/}
                    {/*        /!*                                            required*!/*/}
                    {/*        /!*                                        >*!/*/}
                    {/*        /!*                                            <MenuItem value="" disabled>Оберіть*!/*/}
                    {/*        /!*                                                вік</MenuItem>*!/*/}
                    {/*        /!*                                            {ageArray.map(el => (*!/*/}
                    {/*        /!*                                                <MenuItem key={el.value}*!/*/}
                    {/*        /!*                                                          value={el.value}>{el.label}</MenuItem>*!/*/}
                    {/*        /!*                                            ))}*!/*/}
                    {/*        /!*                                        </Select>*!/*/}
                    {/*        /!*                                    </FormControl>*!/*/}
                    {/*        /!*                                </Grid>*!/*/}
                    {/*        /!*                            ))}*!/*/}
                    {/*        /!*                        </Grid>*!/*/}
                    {/*        /!*                    </>*!/*/}
                    {/*        /!*                )}*!/*/}

                    {/*        /!*                <Grid container sx={{*!/*/}
                    {/*        /!*                    position: isMobile ? 'sticky' : 'static',*!/*/}
                    {/*        /!*                    bottom: 0,*!/*/}
                    {/*        /!*                    background: 'white',*!/*/}
                    {/*        /!*                    zIndex: 1000,*!/*/}
                    {/*        /!*                    p: 1*!/*/}
                    {/*        /!*                }}>*!/*/}
                    {/*        /!*                    <Button variant="outlined" fullWidth*!/*/}
                    {/*        /!*                            onClick={() => setOpenDialogPeople(false)}>Готово</Button>*!/*/}
                    {/*        /!*                </Grid>*!/*/}
                    {/*        /!*            </Stack>*!/*/}
                    {/*        /!*        </Container>*!/*/}
                    {/*        /!*    </Dialog>*!/*/}
                    {/*        /!*</Grid>*!/*/}
                    {/*        /!* Classification *!/*/}
                    {/*        <Grid item sx={{my: 1}} xs={12}>*/}
                    {/*            <FormControl sx={{width: '100%'}} variant="outlined">*/}
                    {/*                <InputLabel id="classification-label"*/}
                    {/*                            sx={{zIndex: 1, background: 'white', px: 0.5}}>*/}
                    {/*                    Класифікація*/}
                    {/*                </InputLabel>*/}
                    {/*                <Select*/}
                    {/*                    labelId="classification-label"*/}
                    {/*                    id="classification"*/}
                    {/*                    multiple*/}
                    {/*                    value={selectedClassifications}*/}
                    {/*                    onChange={handleClassificationChange}*/}
                    {/*                    renderValue={(selected) => selected.map(id => filter.classification.find(el => el.id === id)?.name).join(', ')}*/}
                    {/*                >*/}
                    {/*                    {filter.classification.map((el) => (*/}
                    {/*                        <MenuItem key={el.id} value={el.id}>*/}
                    {/*                            <Checkbox checked={selectedClassifications.includes(el.id)}/>*/}
                    {/*                            <ListItemText primary={el.name}/>*/}
                    {/*                        </MenuItem>*/}
                    {/*                    ))}*/}
                    {/*                </Select>*/}
                    {/*            </FormControl>*/}
                    {/*        </Grid>*/}
                    {/*        {selectedClassifications.map((classification, index) => (*/}
                    {/*            <input key={index} type="hidden" name="classification" value={classification}/>*/}
                    {/*        ))}*/}

                    {/*        /!* Meals *!/*/}
                    {/*        /!*<Grid item sx={{my: 1}} xs={12}>*!/*/}
                    {/*        /!*    <FormControl sx={{width: '100%'}} variant="outlined">*!/*/}
                    {/*        /!*        <InputLabel id="classification-label"*!/*/}
                    {/*        /!*                    sx={{zIndex: 1, background: 'white', px: 0.5}}>*!/*/}
                    {/*        /!*            Харчування*!/*/}
                    {/*        /!*        </InputLabel>*!/*/}
                    {/*        /!*        <Select*!/*/}
                    {/*        /!*            labelId="meal-select-label"*!/*/}
                    {/*        /!*            id="meal-select"*!/*/}
                    {/*        /!*            multiple*!/*/}
                    {/*        /!*            name="meals"*!/*/}
                    {/*        /!*            value={meals.filter(v => v)} // <<< ВАЖНО: убираем пустые строки*!/*/}
                    {/*        /!*            onChange={handleMealsChange}*!/*/}
                    {/*        /!*            // label="Харчування"*!/*/}
                    {/*        /!*            // displayEmpty*!/*/}
                    {/*        /!*            renderValue={(selected) => {*!/*/}
                    {/*        /!*                if (!selected.length) {*!/*/}
                    {/*        /!*                    return <span style={{color: '#aaa'}}>Харчування</span>; // Серый текст если ничего не выбрано*!/*/}
                    {/*        /!*                }*!/*/}
                    {/*        /!*                return selected.map(opt => mealLabels[opt]).join(', ');*!/*/}
                    {/*        /!*            }}*!/*/}
                    {/*        /!*        >*!/*/}
                    {/*        /!*            {Object.entries(mealLabels).map(([key, label]) => (*!/*/}
                    {/*        /!*                <MenuItem key={key} value={key}>*!/*/}
                    {/*        /!*                    <Checkbox checked={meals.includes(key)}/>*!/*/}
                    {/*        /!*                    <ListItemText primary={label}/>*!/*/}
                    {/*        /!*                </MenuItem>*!/*/}
                    {/*        /!*            ))}*!/*/}
                    {/*        /!*        </Select>*!/*/}

                    {/*        /!*    </FormControl>*!/*/}


                    {/*        /!*</Grid>*!/*/}
                    {/*        {meals.map((meal, index) => (*/}
                    {/*            <input key={index} type="hidden" name="meals" value={meal}/>*/}
                    {/*        ))}*/}

                    {/*        /!* Pets Allowed *!/*/}
                    {/*        <Grid item sx={{my: 1}} xs={12}>*/}
                    {/*            <FormControlLabel*/}
                    {/*                control={*/}
                    {/*                    <Checkbox*/}
                    {/*                        name="pets_allowed"*/}
                    {/*                        checked={petsAllowed}*/}
                    {/*                        onChange={(e) => {*/}
                    {/*                            const isChecked = e.target.checked;*/}
                    {/*                            setPetsAllowed(isChecked); // Локально обновляем стейт (для UI)*/}
                    {/*                            updateFilter('pets_allowed', isChecked.toString()); // Меняем URL*/}
                    {/*                        }}*/}
                    {/*                    />*/}
                    {/*                }*/}
                    {/*                label="Можна з тваринами"*/}
                    {/*            />*/}
                    {/*        </Grid>*/}

                    {/*        /!* Price Slider *!/*/}
                    {/*        <Grid item sx={{my: 1}} xs={12}>*/}
                    {/*            <Box>Ваш бюджет</Box>*/}
                    {/*            <Box sx={{width: '100%'}} className="flex stickSides">*/}
                    {/*                <Box>*/}
                    {/*                    {sliderValue[0]} UAH - {sliderValue[1]} UAH*/}
                    {/*                </Box>*/}
                    {/*            </Box>*/}
                    {/*            <Box>*/}
                    {/*                <CustomSlider*/}
                    {/*                    value={sliderValue}*/}
                    {/*                    onChange={(event, newValue) => {*/}
                    {/*                        if (Array.isArray(newValue)) {*/}
                    {/*                            setSliderValue(newValue); // Обновляем локально для UI*/}
                    {/*                        }*/}
                    {/*                    }}*/}
                    {/*                    onChangeCommitted={(event, newValue) => {*/}
                    {/*                        if (Array.isArray(newValue)) {*/}
                    {/*                            // Только когда пользователь отпустил мышку — обновляем URL*/}
                    {/*                            updateFilter('price_from', newValue[0]);*/}
                    {/*                            updateFilter('price_to', newValue[1]);*/}
                    {/*                        }*/}
                    {/*                    }}*/}
                    {/*                    disableSwap*/}
                    {/*                    valueLabelDisplay="auto"*/}
                    {/*                    marks={sliderMarks}*/}
                    {/*                    min={filter.price[0]}*/}
                    {/*                    max={filter.price[filter.price.length - 1]}*/}
                    {/*                />*/}
                    {/*            </Box>*/}
                    {/*        </Grid>*/}

                    {/*        /!*<Grid item sx={{my: 1}} xs={12}>*!/*/}
                    {/*        /!*    <Button fullWidth type="submit" variant="contained">застосувати</Button>*!/*/}
                    {/*        /!*</Grid>*!/*/}
                    {/*    </Grid>*/}

                    {/*    /!* Скрытые input для других searchParams *!/*/}
                    {/*    {Object.keys(filteredObject).map((key) => {*/}
                    {/*        const values = filteredObject[key];*/}
                    {/*        return values.map((value, index) => (*/}
                    {/*            <input type="hidden" name={key} value={value} key={index}/>*/}
                    {/*        ));*/}
                    {/*    })}*/}
                    {/*    /!*</Form>*!/*/}
                    {/*</Grid>*/}

                    <Grid item container xs={12} sm={8}>
                        {/* Если отели есть, но у всех нет matching_children и remaining_children — показать сообщение */}
                        {Array.isArray(filteredData) &&
                            filteredData.every(el =>
                                (!el.hotel.matching_children || el.hotel.matching_children.length === 0) &&
                                (!el.hotel.remaining_children || el.hotel.remaining_children.length === 0) &&
                                !el.hotel.highlight_only
                            ) && (
                                <Typography variant="h6" sx={{mt: 4, mx: 'auto'}}>
                                    Нічого не знайдено за заданими параметрами 😕
                                </Typography>
                            )}

                        {/* Выводим одиночный отель с флагом highlight_only */}
                        {Array.isArray(filteredData) &&
                            filteredData.map((el, index) => {
                                const hotel = el.hotel;
                                if (!hotel.highlight_only) return null;

                                return (
                                    <Grid
                                        container
                                        component={Paper}
                                        sx={{
                                            p: 2,
                                            my: 2,
                                            borderRadius: '15px',
                                            boxShadow: 6,
                                            border: '3px solid #1976d2',
                                            backgroundColor: '#e3f2fd',
                                        }}
                                        key={index}
                                        xs={12}
                                    >
                                        {/* Image */}
                                        <Grid item xs={12} md={4}>
                                            <Box>
                                                <PlaceholderedImage
                                                    key={hotel.image}
                                                    src={hotel.image}
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        borderRadius: '15px',
                                                    }}
                                                />
                                            </Box>
                                        </Grid>

                                        {/* Info */}
                                        <Grid item xs={12} md={8}>
                                            <Box sx={{pl: 2}}>
                                                <Stack direction="column" spacing={1}>
                                                    <Box className="flex stickSides" sx={{justifyContent: 'center'}}>
                                                        <Typography variant="h7" sx={{
                                                            fontWeight: 'bold',
                                                            color: '#09213e',
                                                            textAlign: 'center'
                                                        }}>
                                                            {hotel.name}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                                        <Typography variant="body2" color="textSecondary">
                                                            {hotel.location.text_address}, {hotel.location.city.city}
                                                        </Typography>
                                                    </Box>

                                                    {/* Icons */}
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            flexWrap: 'wrap',
                                                            gap: '10px',
                                                            padding: '10px',
                                                            backgroundColor: '#ffffff',
                                                            borderRadius: '10px',
                                                            overflowX: 'auto',
                                                        }}
                                                    >
                                                        {hotel.icons.map((icon, iconIndex) => (
                                                            <Box
                                                                key={iconIndex}
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    width: '35px',
                                                                    height: '35px',
                                                                    backgroundColor: '#ffffff',
                                                                    borderRadius: '10px',
                                                                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                                                                }}
                                                            >
                                                                <Tooltip title={icon.name} arrow>
                                                                    <img
                                                                        src={icon.icon}
                                                                        alt={icon.name}
                                                                        style={{
                                                                            width: '25px',
                                                                            height: '25px',
                                                                            objectFit: 'contain',
                                                                        }}
                                                                    />
                                                                </Tooltip>
                                                            </Box>
                                                        ))}
                                                    </Box>

                                                    {/*<Typography variant="body2" sx={{fontStyle: 'italic'}}>*/}
                                                    {/*    Ціни тимчасово недоступні*/}
                                                    {/*</Typography>*/}
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        sx={{width: 'fit-content', mt: 1}}
                                                        onClick={() => navigate(hotelDetailsPath(hotel, stringifiedSearch))}
                                                    >
                                                        Детальніше
                                                    </Button>
                                                </Stack>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                );
                            })}

                        {/* Отрисовка отелей с matching_children */}
                        {Array.isArray(filteredData) &&
                            filteredData.some(el => el.hotel.matching_children?.length > 0) &&
                            filteredData.map((el, index) => {
                                const hotel = el.hotel;

                                if (!hotel.matching_children || hotel.matching_children.length === 0) return null;

                                // const firstChild = hotel.matching_children[0];
                                // const firstChild = hotel.matching_children?.find(ch => ch.total_price > 0)
                                //     || hotel.matching_children?.[0];
                                const children = hotel.matching_children || [];

                                const pricedChild = children.find(ch => ch.total_price > 0) || null;
                                const fallbackChild = children[0] || null;

// мінімальна кількість ночей серед усіх тарифів з min_leave
                                const minNights = children.reduce((min, ch) => {
                                    if (!Array.isArray(ch.rates)) return min;

                                    ch.rates.forEach((rate) => {
                                        if (!rate?.min_leave) return;
                                        const n = Number(rate.min_leave_night || 0);
                                        if (!n) return;
                                        if (min == null || n < min) min = n;
                                    });

                                    return min;
                                }, null);

// показуємо "Від N ночей", якщо цін немає, але є обмеження по min_leave
// і поточна кількість ночей менша за мінімальну
                                const showMinNights =
                                    !pricedChild && minNights != null && numNights != null && numNights < minNights;

                                const priceChild = pricedChild || fallbackChild;

                                return (
                                    <NavLink
                                        to={hotelDetailsPath(hotel, stringifiedSearch)}
                                        key={index}
                                        style={{textDecoration: 'none', display: 'block'}}
                                    >
                                        <Grid
                                            container
                                            component={Paper}
                                            sx={{
                                                p: 2,
                                                my: 2,
                                                borderRadius: '15px',
                                                boxShadow: 3,
                                                '&:hover': {boxShadow: 6},
                                            }}
                                            xs={12}
                                        >
                                            {/* Image Section */}
                                            <Grid item xs={12} md={4}>
                                                <Box>
                                                    <PlaceholderedImage
                                                        key={hotel.image}
                                                        src={hotel.image}
                                                        style={{
                                                            width: '100%',
                                                            height: '100%',
                                                            borderRadius: '15px',
                                                        }}
                                                    />
                                                </Box>
                                            </Grid>

                                            {/* Info Section */}
                                            <Grid item xs={12} md={8}>
                                                <Box sx={{pl: 2}}>
                                                    <Stack direction="column" spacing={1}>
                                                        <Box className="flex stickSides"
                                                             sx={{justifyContent: 'center'}}>
                                                            <Typography variant="h7" sx={{
                                                                fontWeight: 'bold',
                                                                color: '#09213e',
                                                                textAlign: 'center'
                                                            }}>
                                                                {hotel.name}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 1
                                                        }}>
                                                            <Typography variant="body2"
                                                                        color="textSecondary">
                                                                {hotel.location.text_address}, {hotel.location.city.city}
                                                            </Typography>
                                                        </Box>

                                                        {/* Icons */}
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                flexWrap: 'wrap',
                                                                gap: '10px',
                                                                padding: '10px',
                                                                backgroundColor: '#ffffff',
                                                                borderRadius: '10px',
                                                                overflowX: 'auto',
                                                            }}
                                                        >
                                                            {hotel.icons.map((icon, iconIndex) => (
                                                                <Box
                                                                    key={iconIndex}
                                                                    sx={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        width: '35px',
                                                                        height: '35px',
                                                                        backgroundColor: '#ffffff',
                                                                        borderRadius: '10px',
                                                                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                                                                    }}
                                                                >
                                                                    <Tooltip title={icon.name} arrow>
                                                                        <img
                                                                            src={icon.icon}
                                                                            alt={icon.name}
                                                                            style={{
                                                                                width: '25px',
                                                                                height: '25px',
                                                                                objectFit: 'contain',
                                                                            }}
                                                                        />
                                                                    </Tooltip>
                                                                </Box>
                                                            ))}
                                                        </Box>

                                                        {/*<Box>*/}
                                                        {/*    <Typography>{firstChild.name}</Typography>*/}
                                                        {/*</Box>*/}
                                                        <Box>
                                                            <Typography>
                                                                {priceChild ? priceChild.name : ''}
                                                            </Typography>
                                                        </Box>

                                                        {/* Price and Taxes */}
                                                        {/*<Box*/}
                                                        {/*    sx={{*/}
                                                        {/*        display: 'flex',*/}
                                                        {/*        justifyContent: 'space-between',*/}
                                                        {/*        alignItems: 'center',*/}
                                                        {/*        mt: 2,*/}
                                                        {/*    }}*/}
                                                        {/*>*/}
                                                        {/*    <Box sx={{*/}
                                                        {/*        display: 'flex',*/}
                                                        {/*        flexDirection: 'column'*/}
                                                        {/*    }}>*/}
                                                        {/*        <Typography variant="h6"*/}
                                                        {/*                    sx={{fontWeight: 'bold'}}>*/}
                                                        {/*            UAH {firstChild.total_price}*/}
                                                        {/*        </Typography>*/}
                                                        {/*        <Typography variant="caption"*/}
                                                        {/*                    color="textSecondary">*/}
                                                        {/*            + UAH {firstChild.total_tax} податків та*/}
                                                        {/*            зборів*/}
                                                        {/*        </Typography>*/}
                                                        {/*    </Box>*/}
                                                        {/*</Box>*/}
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                alignItems: 'center',
                                                                mt: 2,
                                                            }}
                                                        >
                                                            <Box sx={{display: 'flex', flexDirection: 'column'}}>
                                                                {showMinNights ? (
                                                                    <Chip
                                                                        size="small"
                                                                        variant="outlined"
                                                                        color="primary"
                                                                        label={`тільки від ${minNights} ${pluralNightsUa(minNights)}`}
                                                                        sx={{
                                                                            fontWeight: 500,
                                                                            fontSize: '0.85rem',
                                                                            borderWidth: 1.5,
                                                                            padding: '2px 6px',
                                                                            borderRadius: '12px',
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <>
                                                                        {priceChild && (
                                                                            <>
                                                                                <Typography variant="h6"
                                                                                            sx={{fontWeight: 'bold'}}>
                                                                                    UAH {priceChild.total_price}
                                                                                </Typography>
                                                                                <Typography variant="caption"
                                                                                            color="textSecondary">
                                                                                    +
                                                                                    UAH {priceChild.total_tax} податків
                                                                                    та зборів
                                                                                </Typography>
                                                                            </>
                                                                        )}
                                                                    </>
                                                                )}
                                                            </Box>
                                                        </Box>

                                                        <Button
                                                            fullWidth
                                                            variant="contained"
                                                            endIcon={<ArrowRightAltIcon/>}
                                                            sx={{
                                                                mt: 2,
                                                                transform: 'scale(0.8)',
                                                                transformOrigin: 'center',
                                                            }}
                                                        >
                                                            Показати наявні місця
                                                        </Button>
                                                    </Stack>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </NavLink>
                                );
                            })}

                        {/* Заголовок для других вариантов */}
                        {Array.isArray(filteredData) &&
                            filteredData.some(el => el.hotel.remaining_children?.length > 0) && (
                                <Box sx={{mt: 6, mb: 2}}>
                                    <Divider/>
                                    <Typography variant="h6" sx={{
                                        mt: 3,
                                        mb: 1,
                                        fontWeight: 'bold',
                                        textAlign: 'center',
                                        color: '#666'
                                    }}>
                                        Варіанти розміщення, які вас також можуть зацікавити
                                    </Typography>
                                    <Divider/>
                                </Box>
                            )}

                        {/* Отрисовка отелей с remaining_children */}
                        {Array.isArray(filteredData) &&
                            filteredData.some(el => el.hotel.remaining_children?.length > 0) &&
                            filteredData.map((el, index) => {
                                const hotel = el.hotel;

                                if (!hotel.remaining_children || hotel.remaining_children.length === 0) return null;

                                // const firstChild = hotel.remaining_children[0];
                                // const firstChild = hotel.remaining_children?.find(ch => ch.total_price > 0)
                                //     || hotel.remaining_children?.[0];
                                const children = hotel.remaining_children || [];

                                const pricedChild = children.find(ch => ch.total_price > 0) || null;
                                const fallbackChild = children[0] || null;

                                const minNights = children.reduce((min, ch) => {
                                    if (!Array.isArray(ch.rates)) return min;

                                    ch.rates.forEach((rate) => {
                                        if (!rate?.min_leave) return;
                                        const n = Number(rate.min_leave_night || 0);
                                        if (!n) return;
                                        if (min == null || n < min) min = n;
                                    });

                                    return min;
                                }, null);

                                const showMinNights =
                                    !pricedChild && minNights != null && numNights != null && numNights < minNights;

                                const priceChild = pricedChild || fallbackChild;


                                return (
                                    <NavLink
                                        to={hotelDetailsPath(hotel, stringifiedSearch)}
                                        key={index}
                                        style={{textDecoration: 'none', display: 'block'}}
                                    >
                                        <Grid
                                            container
                                            component={Paper}
                                            sx={{
                                                p: 2,
                                                my: 2,
                                                borderRadius: '15px',
                                                boxShadow: 3,
                                                '&:hover': {boxShadow: 6},
                                            }}
                                            xs={12}
                                        >
                                            {/* Image Section */}
                                            <Grid item xs={12} md={4}>
                                                <Box>
                                                    <PlaceholderedImage
                                                        key={hotel.image}
                                                        src={hotel.image}
                                                        style={{
                                                            width: '100%',
                                                            height: '100%',
                                                            borderRadius: '15px',
                                                        }}
                                                    />
                                                </Box>
                                            </Grid>

                                            {/* Info Section */}
                                            <Grid item xs={12} md={8}>
                                                <Box sx={{pl: 2}}>
                                                    <Stack direction="column" spacing={1}>
                                                        <Box className="flex stickSides"
                                                             sx={{justifyContent: 'center'}}>
                                                            <Typography variant="h7" sx={{
                                                                fontWeight: 'bold',
                                                                color: '#09213e',
                                                                textAlign: 'center'
                                                            }}>
                                                                {hotel.name}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 1
                                                        }}>
                                                            <Typography variant="body2"
                                                                        color="textSecondary">
                                                                {hotel.location.text_address}, {hotel.location.city.city}
                                                            </Typography>
                                                        </Box>

                                                        {/* Icons */}
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                flexWrap: 'wrap',
                                                                gap: '10px',
                                                                padding: '10px',
                                                                backgroundColor: '#ffffff',
                                                                borderRadius: '10px',
                                                                overflowX: 'auto',
                                                            }}
                                                        >
                                                            {hotel.icons.map((icon, iconIndex) => (
                                                                <Box
                                                                    key={iconIndex}
                                                                    sx={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        width: '35px',
                                                                        height: '35px',
                                                                        backgroundColor: '#ffffff',
                                                                        borderRadius: '10px',
                                                                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                                                                    }}
                                                                >
                                                                    <Tooltip title={icon.name} arrow>
                                                                        <img
                                                                            src={icon.icon}
                                                                            alt={icon.name}
                                                                            style={{
                                                                                width: '25px',
                                                                                height: '25px',
                                                                                objectFit: 'contain',
                                                                            }}
                                                                        />
                                                                    </Tooltip>
                                                                </Box>
                                                            ))}
                                                        </Box>

                                                        {/*<Box>*/}
                                                        {/*    <Typography>{firstChild.name}</Typography>*/}
                                                        {/*</Box>*/}
                                                        <Box>
                                                            <Typography>
                                                                {priceChild ? priceChild.name : ''}
                                                            </Typography>
                                                        </Box>

                                                        {/* Price and Taxes */}
                                                        {/*<Box*/}
                                                        {/*    sx={{*/}
                                                        {/*        display: 'flex',*/}
                                                        {/*        justifyContent: 'space-between',*/}
                                                        {/*        alignItems: 'center',*/}
                                                        {/*        mt: 2,*/}
                                                        {/*    }}*/}
                                                        {/*>*/}
                                                        {/*    <Box sx={{*/}
                                                        {/*        display: 'flex',*/}
                                                        {/*        flexDirection: 'column'*/}
                                                        {/*    }}>*/}
                                                        {/*        <Typography variant="h6"*/}
                                                        {/*                    sx={{fontWeight: 'bold'}}>*/}
                                                        {/*            UAH {firstChild.total_price}*/}
                                                        {/*        </Typography>*/}
                                                        {/*        <Typography variant="caption"*/}
                                                        {/*                    color="textSecondary">*/}
                                                        {/*            + UAH {firstChild.total_tax} податків та*/}
                                                        {/*            зборів*/}
                                                        {/*        </Typography>*/}
                                                        {/*    </Box>*/}
                                                        {/*</Box>*/}
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                alignItems: 'center',
                                                                mt: 2,
                                                            }}
                                                        >
                                                            <Box sx={{display: 'flex', flexDirection: 'column'}}>
                                                                {showMinNights ? (
                                                                    <Chip
                                                                        size="small"
                                                                        variant="outlined"
                                                                        color="primary"
                                                                        label={`тільки від ${minNights} ${pluralNightsUa(minNights)}`}
                                                                        sx={{
                                                                            fontWeight: 500,
                                                                            fontSize: '0.85rem',
                                                                            borderWidth: 1.5,
                                                                            padding: '2px 6px',
                                                                            borderRadius: '12px',
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <>
                                                                        {priceChild && (
                                                                            <>
                                                                                <Typography variant="h6"
                                                                                            sx={{fontWeight: 'bold'}}>
                                                                                    UAH {priceChild.total_price}
                                                                                </Typography>
                                                                                <Typography variant="caption"
                                                                                            color="textSecondary">
                                                                                    +
                                                                                    UAH {priceChild.total_tax} податків
                                                                                    та зборів
                                                                                </Typography>
                                                                            </>
                                                                        )}
                                                                    </>
                                                                )}
                                                            </Box>
                                                        </Box>


                                                        <Button
                                                            fullWidth
                                                            variant="contained"
                                                            endIcon={<ArrowRightAltIcon/>}
                                                            sx={{
                                                                mt: 2,
                                                                transform: 'scale(0.8)',
                                                                transformOrigin: 'center',
                                                            }}
                                                        >
                                                            Показати наявні місця
                                                        </Button>
                                                    </Stack>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </NavLink>
                                );
                            })}

                    </Grid>


                </Grid>
            </Box>
        </>
    );
}
export const ChangeNumberComponent = ({handleChangeValue, disableConditionMinus, value, label}) => (
    <>
        <Grid item container xs={6} sx={{alignItems: 'center'}}>
            <Typography>{label}</Typography>
        </Grid>
        <Grid item xs={6}>
            <Stack direction='row' sx={{width: 'fit-content', borderRadius: 1, border: '1px solid black'}}>
                <IconButton
                    sx={{height: '40px', width: '40px'}}
                    onClick={() => handleChangeValue(-1)}
                    disabled={disableConditionMinus}
                >
                    <RemoveIcon/>
                </IconButton>
                <Box
                    sx={{
                        padding: '6px 12px',
                        minWidth: '40px',
                        textAlign: 'center',
                        flexGrow: 1,
                    }}
                >
                    {value}
                </Box>
                <IconButton
                    sx={{height: '40px', width: '40px'}}
                    onClick={() => handleChangeValue(1)}
                >
                    <AddIcon/>
                </IconButton>
            </Stack>
        </Grid>
    </>
);

export const ageArray = [
    {value: 0, label: '0 років'},
    {value: 1, label: '1 рік'},
    {value: 2, label: '2 роки'},
    {value: 3, label: '3 роки'},
    {value: 4, label: '4 роки'},
    {value: 5, label: '5 років'},
    {value: 6, label: '6 років'},
    {value: 7, label: '7 років'},
    {value: 8, label: '8 років'},
    {value: 9, label: '9 років'},
    {value: 10, label: '10 років'},
    {value: 11, label: '11 років'},
    {value: 12, label: '12 років'},
    {value: 13, label: '13 років'},
    {value: 14, label: '14 років'},
    {value: 15, label: '15 років'},
    {value: 16, label: '16 років'},
    {value: 17, label: '17 років'}
];
